import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Upload, DollarSign, Target,
  TrendingUp, Shield, Star, Repeat, Newspaper,
  FileText, LogOut, User, Settings, ShoppingCart, Trophy,
  AlertTriangle, Zap, Users, CreditCard,
  FileCheck, History, BarChart3, Package, ClipboardCheck,
  Building2, Crown, MessageSquare, Handshake, Bell,
  Cpu, Brain, Layers, ArrowRightLeft, TrendingDown,
  Combine, Search, Sparkles, Award, PanelLeftClose, PanelLeftOpen, ClipboardList,
  FlaskConical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/context/BrandingContext';
import { useSidebar } from '@/context/SidebarContext';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const { collapsed, toggle } = useSidebar();

  const menuSections = [
    {
      title: 'COMMAND',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', testId: 'sidebar-dashboard' },
        { icon: Bell, label: 'Action Center', path: '/actions', testId: 'sidebar-actions', badge: '3' },
        { icon: ClipboardList, label: 'My Parts', path: '/my-items', testId: 'sidebar-my-items' },
      ]
    },
    {
      title: 'SERVICES',
      items: [
        { icon: Cpu, label: 'Part Intelligence', path: '/component-intelligence', testId: 'sidebar-comp-intel' },
        { icon: Package, label: 'BOM Intelligence', path: '/bom', testId: 'sidebar-bom', subPaths: ['/import'] },
        { icon: ClipboardCheck, label: 'Design Review', path: '/design-center/validate', testId: 'sidebar-design-validate', subPaths: ['/design-center/project'] },
        { icon: FileCheck, label: 'Bid Analyzer', path: '/1bid', testId: 'sidebar-bid-validator' },
        { icon: Sparkles, label: 'Quote Builder', path: '/project-simulator', testId: 'sidebar-project-simulator' },
      ]
    },
    {
      title: 'COST REDUCTION',
      items: [
        { icon: Star, label: 'Supplier Improvements', path: '/supplier-improvements', testId: 'sidebar-suppliers' },
        { icon: DollarSign, label: 'Suggested Negotiations', path: '/renegotiations', testId: 'sidebar-renegotiations' },
        { icon: TrendingDown, label: 'Buy Now / Hold', path: '/buy-timing', testId: 'sidebar-buy-timing' },
        { icon: Repeat, label: 'Smart Alternates', path: '/alternates', testId: 'sidebar-alternates' },
        { icon: Shield, label: 'Risk Radar', path: '/risks', testId: 'sidebar-risks' },
        { icon: Combine, label: 'Consolidate Orders', path: '/consolidate', testId: 'sidebar-consolidate' },
        { icon: TrendingUp, label: 'Savings Tracker', path: '/savings-tracker', testId: 'sidebar-savings-tracker' },
        { icon: Layers, label: 'Savings Intelligence', path: '/value-delivered', testId: 'sidebar-savings-intel' },
      ]
    },
    {
      title: 'AI SOLUTIONS',
      items: [
        { icon: Brain, label: 'AI Price Forecast', path: '/predictions', testId: 'sidebar-forecast' },
        { icon: Target, label: 'Cost Architect', path: '/cost-architect', testId: 'sidebar-cost-arch' },
        { icon: FlaskConical, label: 'R&D Cost Analysis', path: '/rd-cost-analysis', testId: 'sidebar-rd-cost' },
      ]
    },
    {
      title: 'SOURCING',
      items: [
        { icon: Handshake, label: '1Source', path: '/1source', testId: 'sidebar-1source', highlight: true },
        { icon: ShoppingCart, label: '1Spot Orders', path: '/1spot', testId: 'sidebar-1spot' },
      ]
    },
    {
      title: 'ENGAGE',
      items: [
        { icon: Newspaper, label: 'Market Feed', path: '/news', testId: 'sidebar-news' },
        { icon: Trophy, label: 'Success Stories', path: '/success-stories', testId: 'sidebar-stories' },
        { icon: Crown, label: 'Team Savings', path: '/team-savings', testId: 'sidebar-wins', badge: 'Hero' },
        { icon: MessageSquare, label: 'Feedback', path: '/feedback', testId: 'sidebar-feedback' },
      ]
    },
    {
      title: 'REPORTS & ADMIN',
      items: [
        { icon: FileText, label: 'Reports', path: '/reports', testId: 'sidebar-reports' },
        { icon: Building2, label: 'Company Settings', path: '/admin/company', testId: 'sidebar-company' },
        { icon: Users, label: 'User Management', path: '/admin/users', testId: 'sidebar-users' },
        { icon: CreditCard, label: 'Credits & Plans', path: '/admin/billing', testId: 'sidebar-billing' },
      ]
    },
  ];

  const isActive = (item) => {
    if (location.pathname === item.path) return true;
    if (item.path !== '/dashboard' && location.pathname.startsWith(item.path)) return true;
    if (item.subPaths) {
      return item.subPaths.some(sp => location.pathname.startsWith(sp));
    }
    return false;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className={`${collapsed ? 'w-[68px]' : 'w-64'} h-screen bg-white border-r border-gray-200 flex flex-col shadow-sm transition-all duration-200`}>
        {/* Logo */}
        <div className="p-3 border-b border-gray-200 flex items-center gap-2">
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.companyName || 'Company'} className="h-9 w-9 rounded-xl object-contain flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Zap className="h-5 w-5 text-white" />
              </div>
            )}
            {!collapsed && (
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-gray-900 truncate">{branding.companyName || '1DATA'}</h1>
                <p className="text-[10px] text-gray-500 truncate">powered by 1BUY.AI</p>
              </div>
            )}
          </Link>
          <Button variant="ghost" size="sm" onClick={toggle} className="ml-auto h-7 w-7 p-0 text-gray-400 hover:text-gray-600 flex-shrink-0" data-testid="sidebar-collapse-toggle">
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-2 py-3">
          <div className="space-y-4">
            {menuSections.map((section, sIdx) => (
              <div key={sIdx}>
                {!collapsed && (
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 px-2">{section.title}</p>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const active = isActive(item);
                    const linkContent = (
                      <Link key={item.path} to={item.path} data-testid={item.testId}
                        className={`flex items-center ${collapsed ? 'justify-center px-2' : 'gap-2.5 px-3'} py-1.5 rounded-lg text-[13px] font-medium transition-all
                          ${active ? 'bg-green-50 text-green-700 border-l-3 border-green-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                          ${item.highlight && !active ? 'bg-green-50/50 border border-green-200' : ''}
                        `}
                      >
                        <item.icon className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-green-600' : 'text-gray-400'}`} />
                        {!collapsed && (
                          <>
                            <span className="flex-1 truncate">{item.label}</span>
                            {item.badge && (
                              <Badge className={`text-[9px] px-1 py-0 ${item.badge === 'Hero' ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white' : 'bg-blue-100 text-blue-700'}`}>
                                {item.badge}
                              </Badge>
                            )}
                          </>
                        )}
                      </Link>
                    );

                    if (collapsed) {
                      return (
                        <ShadTooltip key={item.path}>
                          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                          <TooltipContent side="right" className="text-xs">{item.label}</TooltipContent>
                        </ShadTooltip>
                      );
                    }
                    return <React.Fragment key={item.path}>{linkContent}</React.Fragment>;
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* User Profile */}
        <div className="p-2 border-t border-gray-200">
          {collapsed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full h-10 p-0">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-green-100 text-green-700 text-xs">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.role || 'Member'}</p>
                </div>
                <Separator className="my-1" />
                <DropdownMenuItem onClick={() => navigate('/admin/company')}><Settings className="h-4 w-4 mr-2" /> Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600"><LogOut className="h-4 w-4 mr-2" /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-green-100 text-green-700">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.role || 'Member'}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/admin/company')}><Settings className="h-4 w-4 mr-2" /> Settings</DropdownMenuItem>
                <Separator className="my-1" />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600"><LogOut className="h-4 w-4 mr-2" /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Sidebar;
